import { useCallback, useEffect, useMemo, useState } from 'react';
import { rateService } from '../../services/rateService';
import { currencyRateService } from '../../services/currencyRateService';
import { notify } from '../../assets/helpers/toast';

/**
 * useCurrencyConverter
 * ---------------------------------------------------------------------------
 * Encapsula toda la lógica de negocio de la "Calculadora BCV":
 *  - Obtención de la tasa BCV (servicio principal + alternativa de respaldo).
 *  - Cálculo automático USD -> Bs.
 *  - Manejo de la fecha de pago, apertura de la modal y copiado al portapapeles.
 *
 * Está desacoplado de la UI para poder reutilizarse o exportarse a otro
 * repositorio con estructura similar. La única dependencia externa son los
 * servicios `rateService` / `currencyRateService` y el helper `notify`.
 */

/** Formatea un número al formato de moneda local venezolana: 13.425,00 */
export const formatBs = (value) =>
  new Intl.NumberFormat('es-VE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

/** Devuelve la fecha actual en formato ISO AAAA-MM-DD (compatible con <input type="date">). */
const getTodayISO = (date = new Date()) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const DEFAULT_USD_AMOUNT = '150.00';

export default function useCurrencyConverter() {
  const [rate, setRate] = useState(0);
  const [usdAmount, setUsdAmount] = useState(DEFAULT_USD_AMOUNT);
  const [paymentDate, setPaymentDate] = useState(getTodayISO());
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingRate, setIsLoadingRate] = useState(false);
  const [copied, setCopied] = useState(false);

  /**
   * Obtiene la tasa BCV para una fecha dada (formato ISO AAAA-MM-DD).
   *  - Para la fecha de hoy: usa el servicio principal de la tasa del día y,
   *    si no responde, recurre al último registro de la lista de tasas USD.
   *  - Para una fecha anterior: consulta la tasa histórica de esa fecha.
   */
  const fetchRate = useCallback(async (date) => {
    setIsLoadingRate(true);
    try {
      const isToday = !date || date === getTodayISO();

      // Fecha específica (distinta de hoy): se busca la tasa histórica.
      if (!isToday) {
        const byDate = await currencyRateService.getCurrencyRates({
          currency: 'USD',
          date,
          limit: 1,
        });
        const byDateRate = byDate?.data?.result?.[0]?.rate;
        if (byDate?.status === 200 && byDateRate != null) {
          setRate(Number(byDateRate));
        } else {
          setRate(0);
          notify('No se encontró tasa BCV para la fecha seleccionada', 'error');
        }
        return;
      }

      // Opción principal: tasa del día.
      const primary = await rateService.getDollarRate();
      const primaryRate = primary?.data?.rate;
      if (primary?.status === 200 && primaryRate != null) {
        setRate(Number(primaryRate));
        return;
      }

      // Opción alternativa: último registro de la lista de tasas USD.
      const fallback = await currencyRateService.getCurrencyRates({
        currency: 'USD',
        date: '',
        limit: 1,
      });
      const fallbackRate = fallback?.data?.result?.[0]?.rate;
      if (fallback?.status === 200 && fallbackRate != null) {
        setRate(Number(fallbackRate));
        return;
      }

      notify('No se pudo obtener la tasa BCV', 'error');
    } catch (error) {
      notify('No se pudo obtener la tasa BCV', 'error');
    } finally {
      setIsLoadingRate(false);
    }
  }, []);

  // Reinicia el indicador de "copiado" tras 1.5s, limpiando el timeout si el
  // componente se desmonta o se vuelve a copiar antes de que expire.
  useEffect(() => {
    if (!copied) return undefined;
    const timeoutId = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timeoutId);
  }, [copied]);

  /** Monto en USD parseado a número (acepta coma o punto decimal). */
  const usdNumber = useMemo(() => {
    const parsed = parseFloat(String(usdAmount).replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  }, [usdAmount]);

  /** Monto calculado en Bolívares: usdAmount * rate. */
  const bsAmount = useMemo(() => usdNumber * rate, [usdNumber, rate]);

  /** Monto en Bolívares listo para mostrar (13.425,00). */
  const bsAmountFormatted = useMemo(() => formatBs(bsAmount), [bsAmount]);

  /** Tasa lista para mostrar con 4 decimales (89.5055). */
  const rateFormatted = useMemo(() => (rate ? Number(rate).toFixed(4) : ''), [rate]);

  /** Actualiza el monto en USD permitiendo solo dígitos y un separador decimal. */
  const handleUsdAmountChange = useCallback((event) => {
    const raw = event?.target?.value ?? '';
    const sanitized = raw.replace(/[^\d.,]/g, '');
    setUsdAmount(sanitized);
  }, []);

  /** Actualiza la fecha de pago y vuelve a buscar la tasa de esa fecha. */
  const handlePaymentDateChange = useCallback(
    (event) => {
      const value = event?.target?.value ?? '';
      setPaymentDate(value);
      if (value) fetchRate(value);
    },
    [fetchRate]
  );

  // Al abrir: reinicia la fecha a hoy y consulta la tasa del día de forma
  // incondicional, de modo que cada apertura refleje cualquier cambio de tasa
  // hecho en el backend.
  const openModal = useCallback(() => {
    const today = getTodayISO();
    setPaymentDate(today);
    setIsOpen(true);
    fetchRate(today);
  }, [fetchRate]);

  // Al cerrar: cierra la modal y reinicia los valores de búsqueda (fecha a hoy).
  const closeModal = useCallback(() => {
    setIsOpen(false);
    setPaymentDate(getTodayISO());
  }, []);

  // Alterna la modal de forma coherente: siempre reinicia la fecha a hoy y, si
  // se está abriendo, dispara la consulta de la tasa del día.
  const toggleModal = useCallback(() => {
    const today = getTodayISO();
    setPaymentDate(today);
    setIsOpen((prev) => {
      const next = !prev;
      if (next) fetchRate(today);
      return next;
    });
  }, [fetchRate]);

  /** Copia el monto en Bolívares (formato 13.425,00) al portapapeles. */
  const handleCopyToClipboard = useCallback(async () => {
    const value = formatBs(bsAmount);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // Fallback para navegadores sin Clipboard API.
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
    } catch (error) {
      notify('No se pudo copiar el monto', 'error');
    }
  }, [bsAmount]);

  return {
    // Estados
    rate,
    rateFormatted,
    usdAmount,
    bsAmount,
    bsAmountFormatted,
    paymentDate,
    isOpen,
    isLoadingRate,
    copied,
    // Acciones
    fetchRate,
    handleUsdAmountChange,
    handlePaymentDateChange,
    handleCopyToClipboard,
    openModal,
    closeModal,
    toggleModal,
  };
}
